// src/contexts/TasksContext.jsx - ПОЛНОСТЬЮ ИСПРАВЛЕННАЯ ВЕРСИЯ
import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import { tasksAPI, usersAPI } from '../services/api.js';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';

const TasksContext = createContext();

export const useTasks = () => {
  const context = useContext(TasksContext);
  if (!context) {
    throw new Error('useTasks must be used within a TasksProvider');
  }
  return context;
};

export const TasksProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wsLoading, setWsLoading] = useState(false);
  const [error, setError] = useState('');
  const [usersMap, setUsersMap] = useState(new Map());
  const { user: currentUser } = useAuth();
  const { subscribe, unsubscribe, isConnected, send } = useWebSocket();

  // Лимит для usersMap (предотвращение утечки памяти)
  const MAX_USERS_IN_MAP = 1000;
  const usersMapRef = useRef(new Map());
  const pendingUserRequests = useRef(new Set()); // Для предотвращения дублирующих запросов
  const taskRequestsRef = useRef(new Map()); // Кэш задач
  const mountedRef = useRef(true); // Добавляем ref для отслеживания монтирования
  const timersRef = useRef([]); // Добавляем ref для таймеров

  // ========== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ==========
  
  // Очистка старых записей из usersMap
  const cleanupUsersMap = useCallback(() => {
    if (usersMapRef.current.size > MAX_USERS_IN_MAP) {
      const entries = Array.from(usersMapRef.current.entries());
      // Сортируем по времени последнего использования
      const sortedEntries = entries.sort((a, b) => {
        const aLastUsed = a[1].lastUsed || 0;
        const bLastUsed = b[1].lastUsed || 0;
        return bLastUsed - aLastUsed;
      });
      
      const newMap = new Map();
      sortedEntries.slice(0, MAX_USERS_IN_MAP).forEach(([key, value]) => {
        newMap.set(key, value);
      });
      
      usersMapRef.current = newMap;
      setUsersMap(newMap);
    }
  }, []);

  // Обновление времени использования пользователя
  const updateUserLastUsed = useCallback((userId) => {
    const user = usersMapRef.current.get(userId);
    if (user) {
      user.lastUsed = Date.now();
      usersMapRef.current.set(userId, user);
    }
  }, []);

  const loadUsersBatch = useCallback(async (userIds) => {
    const uniqueIds = [...new Set(userIds.filter(id => id && id > 0))];
    if (uniqueIds.length === 0) return usersMapRef.current;

    // Фильтруем ID, которых нет в кэше или они устарели
    const usersToLoad = uniqueIds.filter(id => {
      const user = usersMapRef.current.get(id);
      // Загружаем, если нет в кэше или данные устарели (старше 5 минут)
      return !user || (user.loadedAt && Date.now() - user.loadedAt > 5 * 60 * 1000);
    });

    // Убираем дублирующие запросы
    const uniqueUsersToLoad = usersToLoad.filter(id => !pendingUserRequests.current.has(id));
    
    if (uniqueUsersToLoad.length === 0) {
      // Если все пользователи уже в кэше, просто возвращаем их
      const result = new Map();
      uniqueIds.forEach(id => {
        const user = usersMapRef.current.get(id);
        if (user) {
          result.set(id, user);
          updateUserLastUsed(id);
        }
      });
      return result;
    }

    // Добавляем в список ожидания
    uniqueUsersToLoad.forEach(id => pendingUserRequests.current.add(id));

    try {
      console.log(`🔍 Loading ${uniqueUsersToLoad.length} users in batch...`);
      
      // Разбиваем на чанки по 50 пользователей
      const chunks = [];
      for (let i = 0; i < uniqueUsersToLoad.length; i += 50) {
        chunks.push(uniqueUsersToLoad.slice(i, i + 50));
      }
      
      const allUsers = [];
      
      for (const chunk of chunks) {
        const response = await usersAPI.getUsers({ 
          limit: 100,
          user_ids: chunk.join(',')
        });
        const usersList = response.users || response.data || response;
        if (Array.isArray(usersList)) {
          allUsers.push(...usersList);
        }
      }
      
      // Создаем новую карту на основе текущей
      const newUsersMap = new Map(usersMapRef.current);
      
      // Обновляем данные пользователей
      allUsers.forEach(user => {
        if (user && user.id) {
          const enrichedUser = {
            id: user.id,
            name: user.name || 'Пользователь',
            telegram_first_name: user.telegram_first_name || '',
            telegram_last_name: user.telegram_last_name || '',
            telegram_username: user.telegram_username || '',
            email: user.email || '',
            role: user.role || 'user',
            is_active: user.is_active !== undefined ? user.is_active : true,
            telegram_id: user.telegram_id || null,
            telegram_photo_url: user.telegram_photo_url || null,
            loadedAt: Date.now(),
            lastUsed: Date.now()
          };
          newUsersMap.set(user.id, enrichedUser);
        }
      });
      
      // Добавляем заглушки для пользователей, которых не удалось загрузить
      uniqueUsersToLoad.forEach(userId => {
        if (!newUsersMap.has(userId)) {
          newUsersMap.set(userId, {
            id: userId,
            name: `Пользователь #${userId}`,
            telegram_first_name: '',
            telegram_last_name: '',
            telegram_username: '',
            email: '',
            role: 'user',
            is_active: true,
            error: true,
            loadedAt: Date.now(),
            lastUsed: Date.now()
          });
        }
      });
      
      // Очищаем старые записи
      if (newUsersMap.size > MAX_USERS_IN_MAP) {
        const entries = Array.from(newUsersMap.entries());
        const sortedEntries = entries.sort((a, b) => {
          const aLastUsed = a[1].lastUsed || 0;
          const bLastUsed = b[1].lastUsed || 0;
          return bLastUsed - aLastUsed;
        });
        
        newUsersMap.clear();
        sortedEntries.slice(0, MAX_USERS_IN_MAP).forEach(([key, value]) => {
          newUsersMap.set(key, value);
        });
      }
      
      usersMapRef.current = newUsersMap;
      setUsersMap(newUsersMap);
      
      // Возвращаем только запрошенных пользователей
      const result = new Map();
      uniqueIds.forEach(id => {
        const user = newUsersMap.get(id);
        if (user) {
          result.set(id, { ...user, lastUsed: Date.now() });
        }
      });
      
      return result;
      
    } catch (err) {
      console.error('Error loading users batch:', err);
      
      // В случае ошибки создаем заглушки
      const newUsersMap = new Map(usersMapRef.current);
      uniqueUsersToLoad.forEach(userId => {
        if (!newUsersMap.has(userId)) {
          newUsersMap.set(userId, {
            id: userId,
            name: `Пользователь #${userId}`,
            telegram_first_name: '',
            telegram_last_name: '',
            telegram_username: '',
            email: '',
            role: 'user',
            is_active: true,
            error: true,
            loadedAt: Date.now(),
            lastUsed: Date.now()
          });
        }
      });
      
      usersMapRef.current = newUsersMap;
      setUsersMap(newUsersMap);
      
      const result = new Map();
      uniqueIds.forEach(id => {
        const user = newUsersMap.get(id);
        if (user) {
          result.set(id, user);
        }
      });
      
      return result;
    } finally {
      // Убираем из списка ожидания
      uniqueUsersToLoad.forEach(id => pendingUserRequests.current.delete(id));
    }
  }, [updateUserLastUsed]);

  const getFullUserName = useCallback((user) => {
    if (!user) return 'Не назначен';
    if (user.loading) return 'Загрузка...';
    if (user.error) {
      const firstName = user.telegram_first_name || '';
      const lastName = user.telegram_last_name || '';
      const userName = user.name || '';
      if (firstName && lastName) return `${firstName} ${lastName}`;
      if (firstName) return firstName;
      if (lastName) return lastName;
      if (userName && userName !== 'Неизвестный пользователь') return userName;
      return `Пользователь #${user.id}`;
    }
    const firstName = user.telegram_first_name || '';
    const lastName = user.telegram_last_name || '';
    const userName = user.name || '';
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    if (userName) return userName;
    return `Пользователь #${user.id}`;
  }, []);

  const enrichCommentsWithUsers = useCallback((comments, usersCache) => {
    if (!comments || !Array.isArray(comments)) return [];
    return comments.map(comment => {
      let author = null;
      if (!comment.is_system && comment.user_id) {
        author = usersCache.get(comment.user_id) || {
          id: comment.user_id,
          name: 'Пользователь'
        };
        if (author) {
          updateUserLastUsed(comment.user_id);
        }
      }
      return {
        ...comment,
        author: author,
        author_name: author ? getFullUserName(author) : 'Система',
        text: comment.content || comment.text || ''
      };
    }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [getFullUserName, updateUserLastUsed]);

  const enrichTaskWithUsers = useCallback(async (task) => {
    if (!task || !task.id) {
      console.warn('enrichTaskWithUsers: task without id', task);
      return null;
    }

    const userIds = new Set();
    if (task.assignee_id) userIds.add(task.assignee_id);
    if (task.creator_id) userIds.add(task.creator_id);
    if (task.comments && Array.isArray(task.comments)) {
      task.comments.forEach(comment => {
        if (comment.user_id && !comment.is_system) {
          userIds.add(comment.user_id);
        }
      });
    }
    
    const usersCache = await loadUsersBatch([...userIds]);
    const enrichedTask = { ...task };

    if (task.assignee_id) {
      const assignee = usersCache.get(task.assignee_id);
      enrichedTask.assignee = assignee || {
        id: task.assignee_id,
        name: 'Не назначен'
      };
      if (assignee) updateUserLastUsed(task.assignee_id);
    } else {
      enrichedTask.assignee = { id: null, name: 'Не назначен' };
    }

    if (task.creator_id) {
      const creator = usersCache.get(task.creator_id);
      enrichedTask.creator = creator || {
        id: task.creator_id,
        name: 'Система'
      };
      if (creator) updateUserLastUsed(task.creator_id);
    } else {
      enrichedTask.creator = { id: null, name: 'Система' };
    }

    if (task.comments && Array.isArray(task.comments)) {
      enrichedTask.comments = enrichCommentsWithUsers(task.comments, usersCache);
    } else {
      enrichedTask.comments = [];
    }

    // Кэшируем обогащенную задачу
    taskRequestsRef.current.set(task.id, {
      task: enrichedTask,
      timestamp: Date.now()
    });

    return enrichedTask;
  }, [loadUsersBatch, enrichCommentsWithUsers, updateUserLastUsed]);

  const getTask = useCallback(async (taskId) => {
    // Проверяем кэш (валидность 30 секунд)
    const cached = taskRequestsRef.current.get(taskId);
    if (cached && Date.now() - cached.timestamp < 30000) {
      return cached.task;
    }

    try {
      const taskData = await tasksAPI.getTask(taskId);
      if (!taskData || !taskData.id) {
        throw new Error('Task data is invalid');
      }
      const enrichedTask = await enrichTaskWithUsers(taskData);
      if (!enrichedTask || !enrichedTask.id) {
        throw new Error('Enriched task is invalid');
      }
      return enrichedTask;
    } catch (err) {
      console.error('Error loading task:', err);
      throw err;
    }
  }, [enrichTaskWithUsers]);

  const reloadTask = useCallback(async (taskId) => {
    try {
      // Очищаем кэш для этой задачи
      taskRequestsRef.current.delete(taskId);
      const freshTask = await getTask(taskId);
      setTasks(prev => prev.map(task => 
        task.id === taskId ? freshTask : task
      ));
      return { success: true, task: freshTask };
    } catch (err) {
      console.error('❌ Error reloading task:', err);
      return { success: false, error: err.message };
    }
  }, [getTask]);

  const requestTasksList = useCallback((filters = {}) => {
    if (!isConnected) {
      console.warn('WebSocket not connected, cannot request tasks list');
      return;
    }
    setWsLoading(true);
    const dataToSend = {
      skip: filters.skip || 0,
      limit: filters.limit || 100,
      search: filters.search,
      status: filters.status,
      priority: filters.priority,
      category: filters.category,
      my_tasks: filters.my_tasks || false,
    };
    if (filters.assignee_id) {
      dataToSend.assignee_id = parseInt(filters.assignee_id, 10);
    }
    if (filters.creator_id) {
      dataToSend.creator_id = parseInt(filters.creator_id, 10);
    }
    send({
      type: 'tasks:list',
      data: dataToSend
    });
  }, [isConnected, send]);

  const loadTasks = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        skip: filters.skip || 0,
        limit: filters.limit || 100,
        ...filters
      };
      
      const apiParams = { ...params };
      Object.keys(apiParams).forEach(key => {
        if (apiParams[key] === '' || apiParams[key] === null || apiParams[key] === undefined) {
          delete apiParams[key];
        }
      });
      
      console.log('📋 Loading tasks with API params:', apiParams);
      const response = await tasksAPI.getTasks(apiParams);
      
      let tasksList = [];
      let total = 0;
      if (Array.isArray(response)) {
        tasksList = response;
        total = response.length;
      } else {
        tasksList = response.tasks || response.data || [];
        total = response.total || response.total_count || tasksList.length;
      }
      
      const validTasksList = tasksList.filter(task => task && task.id);
      
      if (validTasksList.length === 0) {
        setTasks([]);
        setTotalTasks(total);
        return { success: true, tasks: [] };
      }
      
      // Очищаем старые задачи из кэша
      const now = Date.now();
      taskRequestsRef.current.forEach((value, key) => {
        if (now - value.timestamp > 60000) { // 1 минута
          taskRequestsRef.current.delete(key);
        }
      });

      const enrichmentPromises = validTasksList.map(task => enrichTaskWithUsers(task));
      const enrichedResults = await Promise.allSettled(enrichmentPromises);
      const successfulTasks = enrichedResults
        .filter(result => result.status === 'fulfilled')
        .map(result => result.value)
        .filter(task => task && task.id);
      
      setTasks(successfulTasks);
      setTotalTasks(total);
      
      // Очищаем usersMap
      cleanupUsersMap();
      
      return { success: true, tasks: successfulTasks };
    } catch (err) {
      console.error('❌ Error loading tasks:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Не удалось загрузить задачи';
      setError(errorMessage);
      setTasks([]);
      setTotalTasks(0);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [enrichTaskWithUsers, cleanupUsersMap]);

  // ========== МЕТОДЫ ДЛЯ РАБОТЫ С ЗАДАЧАМИ ==========
  const addTaskComment = useCallback(async (taskId, content) => {
    try {
      const tempId = `temp-comment-${Date.now()}`;
      const tempComment = {
        id: tempId,
        task_id: taskId,
        user_id: currentUser?.id,
        content: content,
        is_system: false,
        created_at: new Date().toISOString(),
        author_name: 'Вы',
        text: content,
        status: 'sending'
      };
      
      // Оптимистичное обновление
      setTasks(prev => prev.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            comments: [tempComment, ...(task.comments || [])]
          };
        }
        return task;
      }));
      
      const response = await tasksAPI.addComment(taskId, content);
      
      // Получаем свежую задачу для синхронизации
      const freshTask = await getTask(taskId);
      if (!freshTask || !freshTask.id) {
        throw new Error('Invalid task after comment');
      }
      
      setTasks(prev => prev.map(task => 
        task.id === taskId ? freshTask : task
      ));
      
      return { success: true, comment: response };
    } catch (err) {
      console.error('❌ Error adding comment:', err);
      
      // Откатываем оптимистичное обновление
      setTasks(prev => prev.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            comments: (task.comments || []).filter(comment => 
              !comment.id.toString().startsWith('temp-')
            )
          };
        }
        return task;
      }));
      
      const errorMessage = err.response?.data?.detail || 'Не удалось добавить комментарий';
      return { success: false, error: errorMessage };
    }
  }, [getTask, currentUser]);

  const createTask = useCallback(async (taskData) => {
    try {
      const result = await tasksAPI.createTask(taskData);
      
      // ИСПРАВЛЕНИЕ: проверяем наличие ID в ответе
      if (!result || !result.id) {
        console.error('❌ Invalid response from server:', result);
        return { success: false, error: 'Сервер вернул некорректный ответ' };
      }
      
      const newTask = await getTask(result.id);
      setTasks(prev => [newTask, ...prev]);
      setTotalTasks(prev => prev + 1);
      return { success: true, task: newTask, taskId: result.id };
      
    } catch (err) {
      console.error('❌ Error creating task:', err);
      
      // ИСПРАВЛЕНИЕ: убираем опасное предположение о 500 ошибке
      const errorMessage = err.response?.data?.detail || 
                          err.message || 
                          'Не удалось создать задачу';
      
      // Добавляем детальную информацию об ошибке
      if (err.response) {
        console.error('Server response:', {
          status: err.response.status,
          data: err.response.data
        });
      }
      
      return { success: false, error: errorMessage };
    }
  }, [getTask]);


  const updateTask = useCallback(async (taskId, taskData) => {
    const previousTasks = [...tasks];
    try {
      // Оптимистичное обновление
      setTasks(prev => prev.map(task => {
        if (task.id === taskId) {
          return { ...task, ...taskData, updated_at: new Date().toISOString() };
        }
        return task;
      }));

      const updatedTask = await tasksAPI.updateTask(taskId, taskData);
      const enrichedTask = await enrichTaskWithUsers(updatedTask);
      if (!enrichedTask || !enrichedTask.id) {
        throw new Error('Enriched task is invalid');
      }
      
      // Обновляем кэш
      taskRequestsRef.current.set(taskId, {
        task: enrichedTask,
        timestamp: Date.now()
      });
      
      setTasks(prev => prev.map(task => 
        task.id === taskId ? enrichedTask : task
      ));
      return { success: true, task: enrichedTask };
    } catch (err) {
      console.error('Error updating task:', err);
      // Откат при ошибке
      setTasks(previousTasks);
      const errorMessage = err.response?.data?.detail || 'Не удалось обновить задачу';
      return { success: false, error: errorMessage };
    }
  }, [tasks, enrichTaskWithUsers]);

  const deleteTask = useCallback(async (taskId) => {
    try {
      // Оптимистичное удаление
      setTasks(prev => prev.filter(task => task.id !== taskId));
      setTotalTasks(prev => Math.max(0, prev - 1));
      
      await tasksAPI.deleteTask(taskId);
      
      // Удаляем из кэша
      taskRequestsRef.current.delete(taskId);
      
      return { success: true };
    } catch (err) {
      console.error('Error deleting task:', err);
      // Откат при ошибке - перезагружаем задачи
      await loadTasks();
      const errorMessage = err.response?.data?.detail || 'Не удалось удалить задачу';
      return { success: false, error: errorMessage };
    }
  }, [loadTasks]);

  const assignTask = useCallback(async (taskId, assigneeId) => {
    const previousTasks = [...tasks];
    try {
      // Оптимистичное обновление
      setTasks(prev => prev.map(task => {
        if (task.id === taskId) {
          const newAssignee = usersMap.get(assigneeId) || {
            id: assigneeId,
            name: 'Загрузка...'
          };
          return {
            ...task,
            assignee_id: assigneeId,
            assignee: newAssignee,
            status: task.status === 'open' ? 'assigned' : task.status,
            updated_at: new Date().toISOString()
          };
        }
        return task;
      }));

      const updatedTask = await tasksAPI.assignTask(taskId, assigneeId);
      const enrichedTask = await enrichTaskWithUsers(updatedTask);
      if (!enrichedTask || !enrichedTask.id) {
        throw new Error('Enriched task is invalid');
      }
      
      setTasks(prev => prev.map(task => 
        task.id === taskId ? enrichedTask : task
      ));
      return { success: true, task: enrichedTask };
    } catch (err) {
      console.error('Error assigning task:', err);
      setTasks(previousTasks);
      const errorMessage = err.response?.data?.detail || 'Не удалось назначить задачу';
      return { success: false, error: errorMessage };
    }
  }, [tasks, usersMap, enrichTaskWithUsers]);

  const completeTask = useCallback(async (taskId, actualHours = null) => {
    const previousTasks = [...tasks];
    try {
      // Оптимистичное обновление
      setTasks(prev => prev.map(task => {
        if (task.id === taskId) {
          return {
            ...task,
            status: 'completed',
            completed_at: new Date().toISOString(),
            actual_hours: actualHours || task.actual_hours,
            updated_at: new Date().toISOString()
          };
        }
        return task;
      }));

      const updatedTask = await tasksAPI.completeTask(taskId, actualHours);
      const enrichedTask = await enrichTaskWithUsers(updatedTask);
      if (!enrichedTask || !enrichedTask.id) {
        throw new Error('Enriched task is invalid');
      }
      
      setTasks(prev => prev.map(task => 
        task.id === taskId ? enrichedTask : task
      ));
      return { success: true, task: enrichedTask };
    } catch (err) {
      console.error('Error completing task:', err);
      setTasks(previousTasks);
      const errorMessage = err.response?.data?.detail || 'Не удалось завершить задачу';
      return { success: false, error: errorMessage };
    }
  }, [tasks, enrichTaskWithUsers]);

  const loadStats = useCallback(async () => {
    try {
      return await tasksAPI.getStats();
    } catch (err) {
      console.error('Error loading stats:', err);
      return {
        total: 0,
        open: 0,
        assigned: 0,
        in_progress: 0,
        completed: 0,
        overdue: 0
      };
    }
  }, []);

  const getUserTasksStats = useCallback(() => {
    if (!currentUser?.id) {
      return {
        total: 0,
        assigned: 0,
        created: 0,
        completed: 0,
        inProgress: 0,
        overdue: 0
      };
    }

    const userTasks = tasks.filter(task => 
      task.assignee_id === currentUser.id || task.creator_id === currentUser.id
    );

    const stats = {
      total: userTasks.length,
      assigned: userTasks.filter(task => task.assignee_id === currentUser.id).length,
      created: userTasks.filter(task => task.creator_id === currentUser.id).length,
      completed: userTasks.filter(task => task.status === 'completed').length,
      inProgress: userTasks.filter(task => task.status === 'in_progress').length,
      overdue: userTasks.filter(task => {
        if (!task.due_date) return false;
        return new Date(task.due_date) < new Date() && !['completed', 'cancelled'].includes(task.status);
      }).length
    };

    return stats;
  }, [tasks, currentUser]);

  // ========== ОБРАБОТЧИКИ WEB-SOCKET ==========
  const handleTaskCreated = useCallback(async (data) => {
    console.log('📨 WebSocket: task created', data);
    try {
      const freshTask = await getTask(data.task_id);
      setTasks(prev => {
        const exists = prev.some(t => t.id === data.task_id);
        if (exists) {
          return prev.map(t => t.id === data.task_id ? freshTask : t);
        } else {
          return [freshTask, ...prev];
        }
      });
      setTotalTasks(prev => prev + 1);
    } catch (err) {
      console.error('❌ Error handling task created:', err);
    }
  }, [getTask]);

  const handleTaskUpdated = useCallback((data) => {
    console.log('📨 WebSocket: task updated', data);
    setTasks(prev => prev.map(task => 
      task.id === data.task_id 
        ? { ...task, ...data.changes, updated_at: new Date().toISOString() }
        : task
    ));
    // Асинхронно перезагружаем задачу для получения актуальных данных
    setTimeout(() => {
      reloadTask(data.task_id);
    }, 100);
  }, [reloadTask]);

  const handleTaskDeleted = useCallback((data) => {
    console.log('📨 WebSocket: task deleted', data);
    setTasks(prev => prev.filter(task => task.id !== data.task_id));
    setTotalTasks(prev => Math.max(0, prev - 1));
    // Удаляем из кэша
    taskRequestsRef.current.delete(data.task_id);
  }, []);

  const handleTaskListResult = useCallback(async (data) => {
    console.log('📨 WebSocket: tasks list result', data);
    setWsLoading(false);
    if (data.tasks && Array.isArray(data.tasks)) {
      const validTasks = data.tasks.filter(task => task && task.id);
      
      // Очищаем старые задачи из кэша
      const now = Date.now();
      taskRequestsRef.current.forEach((value, key) => {
        if (now - value.timestamp > 60000) {
          taskRequestsRef.current.delete(key);
        }
      });
      
      const enrichedTasks = await Promise.all(validTasks.map(task => enrichTaskWithUsers(task)));
      const filteredTasks = enrichedTasks.filter(task => task && task.id);
      
      setTasks(filteredTasks);
      if (data.total !== undefined) {
        setTotalTasks(data.total);
      }
      
      // Очищаем usersMap
      cleanupUsersMap();
    }
  }, [enrichTaskWithUsers, cleanupUsersMap]);

  // ========== ПОДПИСКИ НА WEB-SOCKET ==========
  useEffect(() => {
    if (!isConnected) return;

    subscribe('task:created', handleTaskCreated);
    subscribe('task:updated', handleTaskUpdated);
    subscribe('task:deleted', handleTaskDeleted);
    subscribe('tasks:list_result', handleTaskListResult);

    return () => {
      unsubscribe('task:created', handleTaskCreated);
      unsubscribe('task:updated', handleTaskUpdated);
      unsubscribe('task:deleted', handleTaskDeleted);
      unsubscribe('tasks:list_result', handleTaskListResult);
      
      // Очищаем кэш при размонтировании
      taskRequestsRef.current.clear();
    };
  }, [isConnected, subscribe, unsubscribe, handleTaskCreated, handleTaskUpdated, 
      handleTaskDeleted, handleTaskListResult]);

  // Очистка при размонтировании
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      usersMapRef.current.clear();
      setUsersMap(new Map());
      taskRequestsRef.current.clear();
      pendingUserRequests.current.clear();
      
      // Очищаем таймеры
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];
    };
  }, []);

  // ========== ЗНАЧЕНИЕ КОНТЕКСТА ==========
  const value = {
    tasks,
    totalTasks,
    loading,
    wsLoading,
    error,
    loadTasks,
    requestTasksList,
    getTask,
    createTask,
    updateTask,
    deleteTask,
    assignTask,
    completeTask,
    loadStats,
    addTaskComment,
    reloadTask,
    getFullUserName,
    getUserTasksStats,
  };

  return (
    <TasksContext.Provider value={value}>
      {children}
    </TasksContext.Provider>
  );
};